import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { css } from "styled-components";

const enum fields {
    rightsReservedText = "rightsReservedText",
    privacyPolicyText = "privacyPolicyText",
    privacyPolicyLink = "privacyPolicyLink",
}

interface Props extends WidgetProps {
    fields: {
        [fields.rightsReservedText]: string;
        [fields.privacyPolicyText]: string;
        [fields.privacyPolicyLink]: LinkFieldValue;
    };
}

export interface FooterEndLineStyle {
    container: InjectableCss;
    box?: InjectableCss;
    tittle?: InjectableCss;
    heading?: InjectableCss;
}
export const footerEndLineStyle: FooterEndLineStyle = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-top: 2px solid #686868;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        max-width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        max-width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        max-width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tittle: {
        css: css`
            margin-top: 14px;
            margin-bottom: 14px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            text-transform: uppercase;
            color: ${getColor("primary.contrast")};
            opacity: 0.6;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 24px;
                        margin-bottom: 14px;
                    `,
                    css`
                        margin-top: 24px;
                        margin-bottom: 14px;
                    `,
                    css`
                        margin-top: 24px;
                        margin-bottom: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            margin-top: 14px;
            margin-bottom: 14px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            text-align: right;
            text-transform: uppercase;

            span {
                color: ${getColor("primary.contrast")} !important;
                opacity: 0.6;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 0;
                        margin-bottom: 29px;
                        text-align: left;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 29px;
                        text-align: left;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 29px;
                        text-align: left;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};
const styles = footerEndLineStyle;

const FooterEndLine: React.FC<Props> = props => {
    const { url, title } = useGetLink(props.fields.privacyPolicyLink);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.tittle}>{props.fields.rightsReservedText}</StyledWrapper>
            <Link {...styles.heading} href={url}>
                {props.fields.privacyPolicyText}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterEndLine,
    definition: {
        group: "Common",
        displayName: "FooterEndLine",
        fieldDefinitions: [
            {
                name: fields.rightsReservedText,
                displayName: "Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.privacyPolicyText,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                isRequired: true,
                sortOrder: 1,
            },
            {
                fieldType: "General",
                name: fields.privacyPolicyLink,
                displayName: "Privacy Policy Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
